import React, { useEffect, useState } from "react";
import { Dialog, DialogActions, DialogContent, DialogTitle, TextField, IconButton, Button, Grid, Table, TableHead, TableRow, TableCell, TableBody, Box, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import CustomDialog from "../../../Common/CustomDialog";
import ButtonContained from "../../../Common/ButtonContained";
import CommonTextField from "../../../Common/CommonTextField";
import CustomPagination from "../../../Common/CommonPagination";
import CommonTable from "../../../Common/CommonTable";
import StyledTableCell from "../../../Common/styledTableCell";
import TextLabel from "../../../Common/CommonTextLabel";
import ErrorHandler from "../../../../Utils/handler";
import { addAccount, deleteAccount, getAccount, updateAccount, } from "../../../../Redux/Actions/action";
import { showLoader } from "../../../../Redux/Slices/loaderSlice";
import DataNotFound from "../../../Common/DataNotFound";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

const headerList = ["No", "Reason", "Edit", "Delete"]

const Account = () => {
    // ---------- Hook -----------
    const dispatch = useDispatch();

    // --------------- State -------------
    const [open, setOpen] = useState(false);
    const [data, setData] = useState({})
    const [accountData, setAccountData] = useState({});
    const [error, setError] = useState({});
    const [isEdit, setIsEdit] = useState(false);
    const [page, setPage] = React.useState(1);
    const [rowsPerPage, setRowsPerPage] = React.useState(5);

    // --------------- handleChange ---------------
    const handleClear = () => {
        setOpen(false);
        setIsEdit(false);
        setData({});
        setError({})
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setData({ ...data, [name]: value.trimStart() });
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(1);
    };

    const validation = () => {
        let isValid = true;
        let error = {}
        if (!data.reason) {
            error.reason = "Reason is required";
            isValid = false;
        }
        setError(error);
        return isValid;
    }

    // --------------- Api ---------------
    const _createUpdateAccount = async () => {
        if (validation()) {
            dispatch(showLoader(true))
            try {
                let body = {
                    reason: data?.reason,
                }
                if (isEdit && data?._id) {
                    body._id = data?._id;
                }
                const fn = isEdit ? updateAccount : addAccount
                const result = await dispatch(fn(body))
                const error = ErrorHandler(result)
                if (error) {
                    toast.success(result?.payload?.message);
                    _getAccount()
                    handleClear()
                }
            } catch (error) {
                toast.error(error?.message || "An unknown error occurred");
            } finally {
                dispatch(showLoader(false));
            }
        }
    }

    const _getAccount = async () => {
        dispatch(showLoader(true))
        let body = {
            page: page,
            limit: rowsPerPage
        }
        try {
            const result = await dispatch(getAccount(body));
            const error = ErrorHandler(result);
            if (error) {
                // toast.success(result?.payload?.message);
                setAccountData(result?.payload?.data);
            }
        } catch (error) {
            toast.error(error);
        } finally {
            dispatch(showLoader(false));
        }
    }

    const _deleteAccount = async (id) => {
        dispatch(showLoader(true))
        try {
            const result = await dispatch(deleteAccount(`/${id}`));
            const error = ErrorHandler(result);
            if (error) {
                toast.success(result?.payload?.message);
                _getAccount()
            }
        } catch (error) {
            toast.error(error);
        } finally {
            dispatch(showLoader(false));
        }
    }

    // ---------- useEffect ------------ 
    useEffect(() => {
        _getAccount()
    }, [page, rowsPerPage])

    return (
        <Grid container spacing={1} xs={12}>
            <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: '20px', fontWeight: 500 }}>Account</Typography>
                    <ButtonContained text={'Add Account'} borderRadius={'5px'} height={'30px'} onClick={() => setOpen(true)} />
                </Box>
            </Grid>
            <Grid item xs={12}>
                <CommonTable
                    headerList={headerList}
                    minWidth={200}
                    sx={{ mt: 2 }}
                >
                    {accountData?.data?.length > 0 ? accountData?.data?.map((account, index) => (
                        <TableRow key={account.id}>
                            <StyledTableCell align="center">{(index % rowsPerPage) + 1 + (page - 1) * rowsPerPage}</StyledTableCell>
                            <StyledTableCell align="center">{account?.reason}</StyledTableCell>
                            <StyledTableCell align="center">
                                <IconButton sx={{ padding: 0 }} onClick={() => {
                                    setOpen(true);
                                    setIsEdit(true);
                                    setData(account)
                                }} >
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </StyledTableCell>
                            <StyledTableCell align="center">
                                <IconButton sx={{ padding: 0 }} onClick={() => {
                                    _deleteAccount(account?._id)
                                }} >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </StyledTableCell>
                        </TableRow>
                    )) :
                        (
                            <TableRow>
                                <StyledTableCell colSpan={8}>
                                    <DataNotFound title={'No Records Found'} icon={<ErrorOutlineIcon color="primary" style={{ fontSize: "3rem" }} />} elevation={0} />
                                </StyledTableCell>
                            </TableRow>
                        )
                    }
                </CommonTable>
                {accountData?.count > 0 &&
                    <Grid item xs={12}>
                        <CustomPagination
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            count={Math.ceil(accountData?.count / rowsPerPage)}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage} />

                    </Grid>
                }
                {
                    open && <CustomDialog
                        isOpen={open} handleClose={handleClear} title={isEdit ? 'Update Account' : 'Add Account'} padding={'10px'}
                        width={400}
                        action={
                            <>
                                <ButtonContained
                                    text={isEdit ? 'Update Account' : 'Add Account'}
                                    onClick={() => {
                                        _createUpdateAccount()
                                    }}
                                    minWidth={'100%'}
                                    marginTop={'10px'}

                                />
                            </>
                        }
                    >
                        <Grid container spacing={2} xs={12}>
                            <Grid item xs={12}>
                                <CommonTextField
                                    value={data?.reason}
                                    width={"100%"}
                                    text="Reason"
                                    type="text"
                                    onChange={(e) => handleInputChange(e)}
                                    placeholder="Enter a Reason"
                                    name="reason"
                                    valid
                                />
                                <TextLabel
                                    title={!data?.reason ? error?.reason : ""}
                                    color="error"
                                />
                            </Grid>
                        </Grid>
                    </CustomDialog>
                }
            </Grid>



        </Grid >
    );
};

export default Account;
